import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

// Mapa de slugs del catálogo real de Fixo a etiquetas legibles
const SERVICE_LABELS = {
  // Nivel A
  "belleza-personal":       "Belleza Personal",
  "carpinteria":            "Carpintería",
  "cerrajeria":             "Cerrajería",
  "decoradores":            "Decoradores",
  "entrenamiento":          "Entrenamiento",
  "herrero":                "Herrero",
  "infantiles":             "Infantiles",
  "instalador-general":     "Instalador General",
  "jardineria":             "Jardinería",
  "mascotas":               "Mascotas",
  "mudanzas":               "Mudanzas",
  "pintores":               "Pintores",
  "servicio-domestico":     "Servicio Doméstico",
  "tapicero":               "Tapicero",
  "tecnologia":             "Tecnología",
  // Nivel B
  "aire-acondicionado":     "Aire Acondicionado",
  "albanileria":            "Albañilería",
  "arquitectura":           "Arquitectura",
  "cuidado-de-mayores":     "Cuidado de Mayores",
  "cuidado-de-ninos":       "Cuidado de Niños",
  "electricista":           "Electricista",
  "mecanica-automotriz":    "Mecánica Automotriz",
  "piletas":                "Piletas",
  "plomeros":               "Plomeros",
  "seguridad":              "Seguridad",
  // Nivel C
  "control-de-plagas":      "Control de Plagas",
  "gasista":                "Gasista",
};

const STATUS_CONFIG = {
  notified: { label: "Pendiente",  color: "#b06a00", bg: "#fff3e0" },
  accepted: { label: "Aceptada",   color: "#1b7f4e", bg: "#e8f7ef" },
  rejected: { label: "Rechazada",  color: "#c0392b", bg: "#fdecea" },
  expired:  { label: "Expirada",   color: "#7b5ea7", bg: "#f3eeff" },
  cancelled:{ label: "Cancelada",  color: "#5c7a8a", bg: "#f0f5f8" },
};

// ─── Timer hook ───────────────────────────────────────────────────────────────
function useCountdown(expiresAt) {
  const calc = useCallback(() => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return null;
    const totalSecs = Math.floor(diff / 1000);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return { mins, secs, totalSecs, urgent: totalSecs < 60 };
  }, [expiresAt]);

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const interval = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(interval);
  }, [calc]);

  return time;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#5c7a8a", bg: "#f0f5f8" };
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
      letterSpacing: "0.05em", color: cfg.color, background: cfg.bg,
    }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

function UrgentTimer({ expiresAt }) {
  const time = useCountdown(expiresAt);

  if (!time) return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      color: "#c0392b", fontSize: 13, fontWeight: 700,
    }}>
      ⏰ Expiró
    </div>
  );

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: time.urgent ? "#fdecea" : "#fff3e0",
      border: `1.5px solid ${time.urgent ? "#c0392b" : "#E7A432"}`,
      borderRadius: 8, padding: "6px 12px",
    }}>
      <span style={{ fontSize: 16 }}>⏱</span>
      <span style={{
        fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums",
        color: time.urgent ? "#c0392b" : "#b06a00",
        animation: time.urgent ? "pulse 1s infinite" : "none",
      }}>
        {String(time.mins).padStart(2, "0")}:{String(time.secs).padStart(2, "0")}
      </span>
      <span style={{ fontSize: 11, color: "#8fa3b1" }}>para aceptar</span>
    </div>
  );
}

function QuoteTimer({ expiresAt }) {
  const time = useCountdown(expiresAt);

  if (!time) return <span style={{ color: "#c0392b", fontSize: 12, fontWeight: 600 }}>Expiró</span>;

  const hrs  = Math.floor(time.totalSecs / 3600);
  const mins = Math.floor((time.totalSecs % 3600) / 60);

  return (
    <span style={{ color: "#b06a00", fontSize: 12, fontWeight: 600 }}>
      ⏳ {hrs > 0 ? `${hrs}h ` : ""}{mins}min restantes
    </span>
  );
}

function RequestCard({ req, onAccept, onReject, loading }) {
  const [expanded, setExpanded] = useState(false);
  const navigate   = useNavigate();
  const isUrgent   = req.request_type === "urgent";
  const isPending  = req.response_status === "notified";
  const isAccepted = req.response_status === "accepted";
  const isQuote    = req.request_type === "quote";

  return (
    <div style={{
      background: "#fff", borderRadius: 14,
      border: `2px solid ${isPending && isUrgent ? "#E7A432" : isPending ? "#cddae4" : "#e0e9ef"}`,
      boxShadow: isPending ? "0 4px 20px rgba(22,50,68,0.09)" : "0 1px 6px rgba(22,50,68,0.05)",
      marginBottom: 14, overflow: "hidden",
      transition: "box-shadow 0.2s",
    }}>
      {/* Header strip */}
      {isPending && isUrgent && (
        <div style={{
          background: "#E7A432", padding: "5px 20px",
          fontSize: 11, fontWeight: 800, color: "#163244",
          letterSpacing: "0.08em", textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          ⚡ Solicitud urgente — respondé rápido
        </div>
      )}

      <div style={{ padding: "18px 20px" }}>
        {/* Top row */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", marginBottom: 12,
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{
                background: isUrgent ? "#163244" : "#e7f0f5",
                color: isUrgent ? "#E7A432" : "#163244",
                fontSize: 11, fontWeight: 700, padding: "2px 9px",
                borderRadius: 10, letterSpacing: "0.04em",
              }}>
                {isUrgent ? "⚡ URGENTE" : "📋 PRESUPUESTO"}
              </span>
              <span style={{ fontSize: 12, color: "#8fa3b1" }}>
                {SERVICE_LABELS[req.service_key] || req.service_key}
              </span>
              <Badge status={req.response_status} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#163244" }}>
              {req.client_name}
            </div>
            <div style={{ fontSize: 12, color: "#5c7a8a", marginTop: 2 }}>
              📍 {req.common_data?.address}
            </div>
          </div>

          {/* Timer */}
          {isPending && isUrgent  && <UrgentTimer expiresAt={req.expires_at} />}
          {isPending && !isUrgent && <QuoteTimer  expiresAt={req.expires_at} />}
        </div>

        {/* Descripción */}
        <div style={{
          background: "#f7fafc", borderRadius: 8, padding: "10px 14px",
          fontSize: 13, color: "#163244", marginBottom: 12,
          borderLeft: "3px solid #cddae4",
        }}>
          {req.common_data?.description}
        </div>

        {/* Detalle expandible */}
        {Object.keys(req.specific_data || {}).length > 0 && (
          <>
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, color: "#5c7a8a", fontWeight: 600,
                padding: 0, marginBottom: expanded ? 10 : 0,
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              {expanded ? "▲ Menos detalles" : "▼ Ver más detalles"}
            </button>
            {expanded && (
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12,
              }}>
                {Object.entries(req.specific_data).map(([k, v]) => (
                  <div key={k} style={{
                    background: "#f0f5f8", borderRadius: 6,
                    padding: "4px 10px", fontSize: 12, color: "#163244",
                  }}>
                    <span style={{ color: "#8fa3b1" }}>{k.replace(/_/g, " ")}: </span>
                    <strong>{String(v)}</strong>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Actions — solicitud pendiente */}
        {isPending && (
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button
              onClick={() => onAccept(req.match_id)}
              disabled={loading}
              style={{
                flex: 1, padding: "11px", borderRadius: 8, border: "none",
                background: "#163244", color: "#fff",
                fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {loading === `accept-${req.match_id}` ? (
                <span style={{
                  width: 14, height: 14, border: "2px solid #fff",
                  borderTopColor: "transparent", borderRadius: "50%",
                  animation: "spin 0.7s linear infinite", display: "inline-block",
                }} />
              ) : isUrgent ? "⚡ Aceptar trabajo" : "📋 Enviar presupuesto"}
            </button>
            <button
              onClick={() => onReject(req.match_id)}
              disabled={loading}
              style={{
                padding: "11px 20px", borderRadius: 8,
                border: "2px solid #e0e9ef", background: "transparent",
                color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
                fontWeight: 600, fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              Rechazar
            </button>
          </div>
        )}

        {/* Actions — solicitud aceptada */}
        {isAccepted && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e0e9ef", display: "flex", gap: 10 }}>
            {isQuote && (
              <button
                onClick={() => navigate("/prestador/presupuestos")}
                style={{
                  flex: 1, padding: "11px", borderRadius: 8,
                  background: "#163244", border: "none", color: "#fff",
                  fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                  fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                Enviar presupuesto
              </button>
            )}
            <button
              onClick={() => navigate(`/chat/${req.request_id}`)}
              style={{
                flex: 1, padding: "11px", borderRadius: 8,
                border: "2px solid #163244", background: "#fff",
                color: "#163244", fontFamily: "Montserrat, sans-serif",
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              💬 Chat con cliente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderRequestsPage() {
  const [requests,  setRequests]  = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter, setFilter]     = useState("pending");
  const [loading, setLoading]   = useState(null);
  const [toast, setToast]       = useState(null);

  // Cargar solicitudes desde GET /matching/provider/requests
  useEffect(() => {
    setLoadingData(true); setLoadError("");
    api.get("/matching/provider/requests")
      .then(res => setRequests(res.data.requests || []))
      .catch(err => {
        console.error("Error cargando solicitudes:", err);
        setLoadError("No se pudieron cargar las solicitudes.");
      })
      .finally(() => setLoadingData(false));
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAccept = async (matchId) => {
    setLoading(`accept-${matchId}`);
    try {
      await api.post(`/matching/provider/requests/${matchId}/accept`);
      setRequests(rs => rs.map(r =>
        r.match_id === matchId ? { ...r, response_status: "accepted" } : r
      ));
      showToast("¡Solicitud aceptada! El cliente fue notificado.");
    } catch (err) {
      showToast(err.response?.data?.error || "Error al aceptar la solicitud.", "error");
    } finally {
      setLoading(null);
    }
  };

  const handleReject = async (matchId) => {
    setLoading(`reject-${matchId}`);
    try {
      await api.post(`/matching/provider/requests/${matchId}/reject`);
      setRequests(rs => rs.map(r =>
        r.match_id === matchId ? { ...r, response_status: "rejected" } : r
      ));
      showToast("Solicitud rechazada.", "info");
    } catch (err) {
      showToast(err.response?.data?.error || "Error al rechazar la solicitud.", "error");
    } finally {
      setLoading(null);
    }
  };

  const FILTERS = [
    { id: "pending",  label: "Pendientes", count: requests.filter(r => r.response_status === "notified").length },
    { id: "accepted", label: "Aceptadas",  count: requests.filter(r => r.response_status === "accepted").length },
    { id: "history",  label: "Historial",  count: requests.filter(r => ["rejected","expired","cancelled"].includes(r.response_status)).length },
  ];

  const filtered = requests.filter(r => {
    if (filter === "pending")  return r.response_status === "notified";
    if (filter === "accepted") return r.response_status === "accepted";
    if (filter === "history")  return ["rejected","expired","cancelled"].includes(r.response_status);
    return true;
  });

  // Ordenar: urgentes primero, luego por notified_at DESC
  const sorted = [...filtered].sort((a, b) => {
    if (a.request_type === "urgent" && b.request_type !== "urgent") return -1;
    if (b.request_type === "urgent" && a.request_type !== "urgent") return  1;
    return new Date(b.notified_at) - new Date(a.notified_at);
  });

  const pendingUrgent = requests.filter(
    r => r.response_status === "notified" && r.request_type === "urgent"
  ).length;

  return (
    <>
      <div style={{ fontFamily: "Montserrat, sans-serif", maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#163244" }}>
              Mis solicitudes
            </h1>
            {pendingUrgent > 0 && (
              <span style={{
                background: "#c0392b", color: "#fff", borderRadius: 20,
                padding: "2px 10px", fontSize: 12, fontWeight: 800,
                animation: "pulse 1.5s infinite",
              }}>
                {pendingUrgent} urgente{pendingUrgent > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <p style={{ margin: 0, color: "#8fa3b1", fontSize: 14 }}>
            Respondé las solicitudes urgentes antes de que expiren.
          </p>
        </div>

        {/* Loading / error */}
        {loadingData && (
          <div style={{ textAlign: "center", padding: "48px", color: "#8fa3b1" }}>
            <span style={{ display: "inline-block", width: 24, height: 24, border: "3px solid #cddae4", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br />
            Cargando solicitudes...
          </div>
        )}
        {loadError && !loadingData && (
          <div style={{ textAlign: "center", padding: "32px", color: "#c0392b", fontSize: 14 }}>
            ⚠ {loadError}
          </div>
        )}

        {/* Filters */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 20,
          background: "#fff", borderRadius: 12, padding: 6,
          boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
        }}>
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                flex: 1, padding: "9px 8px", borderRadius: 8, border: "none",
                background: filter === f.id ? "#163244" : "transparent",
                color: filter === f.id ? "#fff" : "#5c7a8a",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: filter === f.id ? 700 : 500,
                fontSize: 13, cursor: "pointer", transition: "all 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {f.label}
              <span style={{
                background: filter === f.id ? "#E7A432" : "#e0e9ef",
                color: filter === f.id ? "#163244" : "#5c7a8a",
                borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 800,
              }}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        {/* Cards */}
        {sorted.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "48px 20px",
            background: "#fff", borderRadius: 14,
            border: "2px dashed #e0e9ef", color: "#8fa3b1",
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
              No hay solicitudes en esta categoría
            </div>
            <div style={{ fontSize: 13 }}>
              Cuando recibas nuevas solicitudes aparecerán acá.
            </div>
          </div>
        ) : (
          sorted.map(req => (
            <RequestCard
              key={req.match_id}
              req={req}
              onAccept={handleAccept}
              onReject={handleReject}
              loading={loading}
            />
          ))
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "success" ? "#163244" : "#5c7a8a",
          color: "#fff", padding: "12px 24px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          borderLeft: `4px solid ${toast.type === "success" ? "#E7A432" : "#cddae4"}`,
        }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </>
  );
}
